import { type SummaryData, type SummaryDataProvider } from "~/features/configurator/Shaft/SummaryDataProvider";

import { createContext, type ReactNode, useContext } from "react";
import { Stack, styled } from "~/common/mui";
import StarIcon from "@mui/icons-material/Star";

export function renderSummary(data?: SummaryData, colSpan?: number) {
  return (
    <>
      <td colSpan={colSpan}>{data?.name}</td>
      <td colSpan={colSpan}>{renderValue(data)}</td>
    </>
  );
}

export function renderValue(data?: Omit<SummaryData, "name">) {
  if (!data) {
    return null;
  }
  return (
    <Stack direction={"row"} gap={1} className={"d-flex flex-align-center"}>
      {data.isStandard && <StarIcon className={"f-green"} sx={{ fontSize: 15 }} />}
      {data.value}
    </Stack>
  );
}
export function renderStep2MachiningValue(data?: SummaryData) {
  if (!data) {
    return null;
  }
  return (
    <Stack direction={"row"} gap={1}>
      {data.isStandard && <StarIcon className={"f-green"} sx={{ fontSize: 15 }} />}
      {data.value}
      {data.name}
    </Stack>
  );
}

export const Table = ({
  children,
  colAmount = 6,
  className,
}: {
  children?: ReactNode;
  colAmount?: number;
  className?: string;
}) => {
  return (
    <SummaryTable className={className}>
      <colgroup>
        {Array.from({ length: colAmount }, (_, index) => (
          <col key={index} />
        ))}
      </colgroup>
      {children}
    </SummaryTable>
  );
};

const SummaryTable = styled("table")(({ theme }) => ({
  width: "100%",
  ["col:nth-child(odd)"]: {
    width: "17%",
  },
  ["col:nth-child(even)"]: {
    width: "13%",
  },
  [theme.breakpoints.up("desktop-m")]: {
    ["col:nth-child(odd)"]: {
      width: "18%",
    },
    ["col:nth-child(even)"]: {
      width: "12%",
    },
  },
  ["tbody:nth-child(even)"]: {
    backgroundColor: theme.palette.surface.main,
  },
  ["td, th"]: {
    padding: theme.spacing(1, 2),
    verticalAlign: "top",
  },
  th: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const SummaryStepContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(4),
  paddingTop: theme.spacing(4),
  ["> *::after"]: {
    content: '""',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const SummaryProviderContext = createContext<SummaryDataProvider | null>(null);

export const SummaryProviderContextProvider = SummaryProviderContext.Provider;

export const useSummaryProvider = () => {
  const summaryProvider = useContext(SummaryProviderContext);
  if (!summaryProvider) {
    throw new Error("SummaryProviderContext is not provided");
  }
  return summaryProvider;
};
